/**
 * Number Parsing Utilities
 *
 * Shared utilities for parsing formatted number strings from Chakra UI NumberInput components.
 * Strips formatting characters (commas, currency symbols, etc.) before parsing.
 */

/**
 * Parse formatted number strings from Chakra UI NumberInput
 * Strips formatting characters (commas, currency symbols, etc.) before parsing
 *
 * @param value - Formatted string (e.g., "$1,234.56" or "1,234.56")
 * @returns Parsed number or NaN if invalid
 *
 * @example
 * parseFormattedNumber("$1,234.56") // Returns 1234.56
 * parseFormattedNumber("1,234.56")  // Returns 1234.56
 * parseFormattedNumber("")          // Returns NaN
 */
export const parseFormattedNumber = (value: string): number => {
  if (!value || value.trim() === '') return NaN;
  // Remove all non-numeric characters except decimal point and minus sign
  const cleaned = value.replace(/[^\d.-]/g, '');
  return parseFloat(cleaned);
};

/**
 * Validate that a parsed number is finite and valid
 *
 * @param value - Number to validate
 * @returns True if number is finite and not NaN
 *
 * @example
 * isValidNumber(1234.56)  // Returns true
 * isValidNumber(NaN)       // Returns false
 * isValidNumber(Infinity)  // Returns false
 */
export const isValidNumber = (value: number): boolean => {
  return Number.isFinite(value) && !Number.isNaN(value);
};

/**
 * Parse numeric input from form values with defensive handling
 *
 * This utility handles BOTH raw numeric strings AND legacy formatted values,
 * making it safe to use in calculators for backward compatibility with
 * existing database records that may contain formatted values.
 *
 * Expected input (after fix): "57081" (raw numeric string)
 * Legacy input (before fix): "$57,081.00" (formatted currency)
 *
 * @param value - Input string (raw or formatted)
 * @param defaultValue - Value to return if parsing fails (default: 0)
 * @returns Parsed number or defaultValue if invalid
 *
 * @example
 * parseNumericInput("57081")        // Returns 57081
 * parseNumericInput("$57,081.00")   // Returns 57081 (handles legacy format)
 * parseNumericInput("")             // Returns 0
 * parseNumericInput("", 100)        // Returns 100
 * parseNumericInput("invalid")      // Returns 0
 *
 * @see ~/types/form-types.ts - For expected form value formats
 */
export const parseNumericInput = (value: string | undefined | null, defaultValue = 0): number => {
  if (value === undefined || value === null || value.trim() === '') {
    return defaultValue;
  }

  // Remove all non-numeric characters except decimal point and minus sign
  // This handles both raw values ("57081") and formatted values ("$57,081.00")
  const cleaned = value.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);

  return Number.isFinite(parsed) ? parsed : defaultValue;
};

/**
 * Parse percentage input from form values
 *
 * Converts percentage string to decimal (e.g., "2.5" → 0.025).
 * Also handles legacy formatted values (e.g., "2.50%" → 0.025).
 *
 * @param value - Percentage string (e.g., "2.5" for 2.5%)
 * @param defaultValue - Value to return if parsing fails (default: 0)
 * @returns Decimal value (e.g., 0.025 for 2.5%)
 *
 * @example
 * parsePercentageInput("2.5")    // Returns 0.025
 * parsePercentageInput("2.50%")  // Returns 0.025 (handles legacy format)
 * parsePercentageInput("")       // Returns 0
 */
export const parsePercentageInput = (
  value: string | undefined | null,
  defaultValue = 0
): number => {
  const numValue = parseNumericInput(value, defaultValue * 100);
  return numValue / 100;
};

// ============================================
// Form Input Sanitization (for storage)
// ============================================

/**
 * Check if a string looks like a formatted numeric value
 * (contains currency symbols, commas, or percent signs)
 */
const isFormattedNumericString = (value: string): boolean => {
  // Check if it contains formatting characters that need stripping
  return /[$,%]/.test(value) || /\d,\d/.test(value);
};

/**
 * Sanitize a numeric string by stripping formatting
 *
 * Converts formatted currency/percentage/number strings to raw numeric strings
 * for storage. Preserves empty strings and non-numeric strings as-is.
 *
 * @param value - Potentially formatted string
 * @returns Raw numeric string or original value if not numeric
 *
 * @example
 * sanitizeNumericString("$57,081.00")  // Returns "57081"
 * sanitizeNumericString("2.50%")       // Returns "2.5"
 * sanitizeNumericString("1,000")       // Returns "1000"
 * sanitizeNumericString("57081")       // Returns "57081" (already clean)
 * sanitizeNumericString("")            // Returns ""
 * sanitizeNumericString("hello")       // Returns "hello" (not numeric)
 */
export const sanitizeNumericString = (value: string): string => {
  if (!value || value.trim() === '') {
    return value;
  }

  // If it doesn't look like a formatted number, return as-is
  if (!isFormattedNumericString(value)) {
    return value;
  }

  // Strip formatting characters
  const cleaned = value.replace(/[^\d.-]/g, '');

  // Verify it's actually a valid number
  const parsed = parseFloat(cleaned);
  if (!Number.isFinite(parsed)) {
    return value; // Return original if parsing fails
  }

  // Return the cleaned string (preserves decimal precision)
  return cleaned;
};

/**
 * Known numeric fields in form types that should be sanitized
 * Maps field names to their expected format
 */
const NUMERIC_FORM_FIELDS = new Set([
  // DRFInputsForm
  'startingBalance',
  'minimumBalance',
  'maximumBalance',
  // TaxValueInputsForm
  'initialTaxableValue',
  'startYearGrowthRate',
  'fiveYearGrowthRate',
  'twentyYearGrowthRate',
  // MillageInputsForm
  'peakMillage',
  'peakMillageDuration',
  'minimumMillage',
  'minimumMillageDuration',
  // ExistingDebtPaymentForm
  'principalPayment',
  'interestPayment',
  'interestRate',
  // PaymentDateForm
  'revenuePercentage',
]);

/**
 * Deep sanitize form inputs for storage
 *
 * Recursively processes an object and sanitizes all known numeric string fields.
 * Used before sending form data to the database to ensure clean storage format.
 *
 * @param obj - Form inputs object (can be nested with arrays)
 * @returns New object with sanitized numeric strings
 *
 * @example
 * sanitizeFormInputsForStorage({
 *   startingBalance: "$57,081.00",
 *   minimumBalance: "$0.00"
 * })
 * // Returns: { startingBalance: "57081", minimumBalance: "0" }
 */
export const sanitizeFormInputsForStorage = <T>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeFormInputsForStorage(item)) as T;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' && NUMERIC_FORM_FIELDS.has(key)) {
        result[key] = sanitizeNumericString(value);
      } else if (typeof value === 'object') {
        result[key] = sanitizeFormInputsForStorage(value);
      } else {
        result[key] = value;
      }
    }
    return result as T;
  }

  return obj;
};
